import { useCallback, useEffect, useRef, useState } from 'react';
import { useDataSyncText } from 'src/components/star-grid/hooks/useDataSyncText';
import { createDataSync, listDataSyncJobs } from 'src/components/star-grid/services/starGridService';
import { DataSyncJob, DataSyncRequest } from 'src/components/star-grid/starGridTypes';
import { ACTIVE_SYNC_STATUSES } from 'src/components/star-grid/utils/dataSync';
import { getCurrentUTCTimeZoneInISO } from 'src/utilities/DateTimeUtilities';

export interface UseDataSyncJobsResult {
  readonly jobs: readonly DataSyncJob[];
  readonly loading: boolean;
  readonly error: string | undefined;
  readonly submitting: boolean;
  readonly activeJob: DataSyncJob | undefined;
  readonly canTrigger: boolean;
  readonly refresh: () => Promise<void>;
  readonly triggerSync: (request: DataSyncRequest) => Promise<DataSyncJob | undefined>;
}

/** Poll active runs quickly and idle history periodically to detect runs started elsewhere. */
export function useDataSyncJobs(): UseDataSyncJobsResult {
  const t = useDataSyncText();
  const [jobs, setJobs] = useState<DataSyncJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const submittingRef = useRef(false);
  const activeRef = useRef(false);
  const mountedRef = useRef(true);

  const refresh = useCallback(async (): Promise<void> => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const next = await listDataSyncJobs(50, controller.signal);
      if (controller.signal.aborted) return;
      activeRef.current = next.some((job) => ACTIVE_SYNC_STATUSES.has(job.status));
      setJobs(next);
      setReady(true);
      setError(undefined);
    } catch (caught) {
      if (controller.signal.aborted) return;
      setReady(false);
      setError(caught instanceof Error ? caught.message : t('unknownError'));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    mountedRef.current = true;
    void refresh();
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, [refresh]);

  const activeJob = jobs.find((job) => ACTIVE_SYNC_STATUSES.has(job.status));
  const canTrigger = ready && !loading && !error && !activeJob && !submitting;

  useEffect(() => {
    const timer = window.setInterval(() => void refresh(), activeJob ? 5000 : 15000);
    return () => window.clearInterval(timer);
  }, [Boolean(activeJob), refresh]);

  const triggerSync = useCallback(
    async (request: DataSyncRequest): Promise<DataSyncJob | undefined> => {
      // A ref closes the double-click window before React renders the disabled button.
      if (!canTrigger || submittingRef.current || activeRef.current) return undefined;
      submittingRef.current = true;
      setSubmitting(true);
      try {
        const response = await createDataSync(request);
        const optimistic: DataSyncJob = {
          id: response.id,
          status: response.status,
          sync_scope: request.sync_scope ?? 'RANGE',
          reporting_scenario_id: request.sync_scope === 'FULL' ? null : request.reporting_scenario_id,
          from_month_id: request.sync_scope === 'FULL' ? null : request.from_month_id,
          to_month_id: request.sync_scope === 'FULL' ? null : request.to_month_id,
          started_at: getCurrentUTCTimeZoneInISO()
        };
        activeRef.current = true;
        if (mountedRef.current) {
          // Discard an older in-flight history response before installing the new job.
          abortRef.current?.abort();
          setJobs((previous) => [optimistic, ...previous]);
          void refresh();
        }
        return optimistic;
      } catch (caught) {
        if (mountedRef.current) void refresh();
        throw caught;
      } finally {
        submittingRef.current = false;
        if (mountedRef.current) setSubmitting(false);
      }
    },
    [canTrigger, refresh]
  );

  return { jobs, loading, error, submitting, activeJob, canTrigger, refresh, triggerSync };
}
